import { answerKey } from "../../utils/regulatoryIdentity"
import { createDeferredAnswerSaves } from "../../utils/deferredAnswerSaves"
import { validateAnswer } from "../../utils/regulatoryAnswers"
import { validateRegulatoryResponse } from "../../utils/regulatoryProtocol"
import { useCallback, useEffect, useRef, useState } from "react"
import { v4 as uuidv4 } from "uuid"
import {
  Equipment,
  RawStorageEntity,
  RegulatoryControlAnswer,
  Site,
  StorageEntityType,
  StorageRequest,
  StorageResponse,
  UCPayload,
  UCPayloadType,
} from "../../generated/proto-ts/main"
import { useRegulatorySession } from "../../providers/regulatoryProvider"
import type { IEN1176AnswerMap } from "../../types"
import { useCloudUsercommContextWS } from "../../usercomm/cloud/cloudUsercommProviderWS"
import { decodeLengthDelimitedArray } from "../../usercomm/common/usercommUtils"
import { regulatoryUuid } from "../../utils/regulatoryPresentation"
import { pbUUIDToUuid, uuidToPbUUID } from "../../utils/utils"
export type AnswerSaveState = { state: "pending" | "saved" | "failed"; error?: string }
export function useRegulatoryRequest() {
  const context = useCloudUsercommContextWS()
  const contextRef = useRef(context)
  contextRef.current = context
  const pending = useRef(
    new Map<
      string,
      {
        resolve: (value: UCPayload) => void
        reject: (reason: Error) => void
        timer: ReturnType<typeof setTimeout>
        type: UCPayloadType
      }
    >(),
  )
  const { sessionKey } = useRegulatorySession()
  useEffect(
    () => () => {
      pending.current.forEach((p) => {
        clearTimeout(p.timer)
        p.reject(new Error("La session a changé ou la page a été fermée."))
      })
      pending.current.clear()
    },
    [sessionKey],
  )
  useEffect(() => {
    for (const payload of context.recvMessageQueue) {
      const uuid = pbUUIDToUuid(payload.uuid)
      const p = pending.current.get(uuid)
      if (!p) continue
      clearTimeout(p.timer)
      pending.current.delete(uuid)
      context.consumeRecvMessage(payload.uuid)
      try {
        validateRegulatoryResponse(p.type, payload)
        p.resolve(payload)
      } catch (reason) {
        p.reject(reason instanceof Error ? reason : new Error(String(reason)))
      }
    }
  }, [context.recvMessageQueue, context.consumeRecvMessage])
  const request = useCallback(
    (type: UCPayloadType, storage: StorageRequest): Promise<UCPayload> =>
      new Promise((resolve, reject) => {
        if (!contextRef.current.socketIsConnected) {
          reject(
            new Error(
              "Connexion cloud indisponible. La connexion BLE ne synchronise pas les réponses réglementaires.",
            ),
          )
          return
        }
        const uuid = uuidv4()
        const timer = setTimeout(() => {
          pending.current.delete(uuid)
          reject(
            new Error(
              "Le serveur n’a pas confirmé l’enregistrement. Vérifiez la connexion puis réessayez.",
            ),
          )
        }, 20000)
        pending.current.set(uuid, { resolve, reject, timer, type })
        contextRef.current.addEmitMessage(
          new UCPayload({ uuid: uuidToPbUUID(uuid), type, data: storage.serializeBinary() }),
        )
      }),
    [],
  )
  return { request, connected: context.socketIsConnected }
}
export function useRegulatoryAnswers(targetUuid: string, entityType: StorageEntityType) {
  const { request, connected } = useRegulatoryRequest()
  const { sessionKey } = useRegulatorySession()
  const key = `${sessionKey}/${entityType}/${targetUuid}`
  const keyRef = useRef(key)
  keyRef.current = key
  const [answers, setAnswers] = useState<IEN1176AnswerMap>({})
  const [states, setStates] = useState<Record<string, AnswerSaveState>>({})
  const [error, setError] = useState<string>()
  const [loading, setLoading] = useState(false)
  const deferred = useRef(createDeferredAnswerSaves())
  const drafts = useRef<IEN1176AnswerMap>({})
  const versions = useRef<Record<string, number>>({})
  const chains = useRef<Record<string, Promise<void>>>({})
  useEffect(() => {
    const queue = deferred.current
    const checkpoint = () => {
      if (document.visibilityState === "hidden") queue.flush()
    }
    document.addEventListener("visibilitychange", checkpoint)
    return () => {
      document.removeEventListener("visibilitychange", checkpoint)
      queue.cancel()
    }
  }, [key])
  const flush = useCallback((point?: string) => deferred.current.flush(point), [])
  const [reloadVersion, reload] = useState(0)
  useEffect(() => {
    setAnswers({})
    setStates({})
    setError(undefined)
    drafts.current = {}
    versions.current = {}
    chains.current = {}
  }, [key])
  useEffect(() => {
    if (!targetUuid || !connected) return
    let live = true
    setLoading(true)
    setError(undefined)
    request(
      UCPayloadType.SICO_REGULATORY_GET_CONTROL_ANSWERS,
      new StorageRequest({
        entity_uuid: uuidToPbUUID(targetUuid),
        entity_type: entityType,
        regulatory_contexts: true,
      }),
    )
      .then((payload) => {
        if (!live || keyRef.current !== key) return
        const values = decodeLengthDelimitedArray(
          RegulatoryControlAnswer,
          StorageResponse.deserializeBinary(payload.data).multiple_entities_data,
        )
        const map: IEN1176AnswerMap = {}
        for (const answer of values) {
          const target =
            entityType === StorageEntityType.SITE ? answer.site_uuid : answer.equipment_uuid
          if (regulatoryUuid(target) === targetUuid) map[answerKey(answer)] = answer
        }
        setAnswers({ ...map, ...drafts.current })
      })
      .catch((reason) => {
        if (live && keyRef.current === key) setError(reason.message)
      })
      .finally(() => {
        if (live && keyRef.current === key) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [key, targetUuid, entityType, connected, request, reloadVersion])
  const save = useCallback(
    (answer: RegulatoryControlAnswer, options?: { debounce?: boolean }) => {
      if (!targetUuid) return
      const point = answerKey(answer)
      const snapshot = answer.clone()
      if (entityType === StorageEntityType.SITE) snapshot.site_uuid = uuidToPbUUID(targetUuid)
      else snapshot.equipment_uuid = uuidToPbUUID(targetUuid)
      const version = (versions.current[point] ?? 0) + 1
      versions.current[point] = version
      drafts.current[point] = snapshot
      setAnswers((prev) => ({ ...prev, [point]: snapshot }))
      setStates((prev) =>
        prev[point]?.state === "pending" ? prev : { ...prev, [point]: { state: "pending" } },
      )
      const enqueue = () => {
        // Serialize writes for each point: a slow previous response must not overwrite a newer edit.
        const previous = chains.current[point] ?? Promise.resolve()
        chains.current[point] = previous
          .catch(() => {})
          .then(async () => {
            if (keyRef.current !== key || versions.current[point] !== version) return
            try {
              const validation = validateAnswer(snapshot)
              if (validation) throw new Error(validation)
              await request(
                UCPayloadType.SICO_REGULATORY_SET_CONTROL_ANSWER,
                new StorageRequest({
                  entity_uuid: uuidToPbUUID(targetUuid),
                  entity_type: entityType,
                  single_entity_data: snapshot.serializeBinary(),
                }),
              )
              if (keyRef.current === key && versions.current[point] === version) {
                delete drafts.current[point]
                setStates((prev) => ({ ...prev, [point]: { state: "saved" } }))
              }
            } catch (reason) {
              if (keyRef.current === key && versions.current[point] === version)
                setStates((prev) => ({
                  ...prev,
                  [point]: {
                    state: "failed",
                    error: reason instanceof Error ? reason.message : String(reason),
                  },
                }))
            }
          })
      }
      if (options?.debounce) deferred.current.queue(point, enqueue)
      else {
        deferred.current.cancel(point)
        enqueue()
      }
    },
    [key, targetUuid, entityType, request],
  )
  const retry = useCallback(
    (point: string) => {
      const answer = drafts.current[point]
      if (answer) save(answer)
    },
    [save],
  )
  const dirty = Object.values(states).some((s) => s.state !== "saved")
  useEffect(() => {
    if (!dirty) return
    const guard = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", guard)
    return () => window.removeEventListener("beforeunload", guard)
  }, [dirty])
  return {
    answers,
    flush,
    states,
    save,
    retry,
    dirty,
    error,
    loading,
    connected,
    reload: () => reload((n) => n + 1),
  }
}

export function useRegulatoryReportAnswers(siteUuid: string) {
  const { request, connected } = useRegulatoryRequest()
  const { sessionKey } = useRegulatorySession()
  const [siteAnswers, setSiteAnswers] = useState<IEN1176AnswerMap>({})
  const [equipmentAnswersMap, setEquipmentAnswers] = useState<Record<string, IEN1176AnswerMap>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>()
  const [version, reload] = useState(0)
  useEffect(() => {
    let live = true
    setSiteAnswers({})
    setEquipmentAnswers({})
    setLoading(true)
    setError(undefined)
    if (!siteUuid || !connected)
      return () => {
        live = false
      }
    request(
      UCPayloadType.SICO_REGULATORY_GET_CONTROL_ANSWERS_RECURSIVE,
      new StorageRequest({
        entity_uuid: uuidToPbUUID(siteUuid),
        entity_type: StorageEntityType.SITE,
        regulatory_contexts: true,
      }),
    )
      .then((payload) => {
        if (!live) return
        const site: IEN1176AnswerMap = {}
        const equipment: Record<string, IEN1176AnswerMap> = {}
        for (const answer of decodeLengthDelimitedArray(
          RegulatoryControlAnswer,
          StorageResponse.deserializeBinary(payload.data).multiple_entities_data,
        )) {
          const id = regulatoryUuid(answer.equipment_uuid)
          const point = answerKey(answer)
          if (id) {
            equipment[id] = equipment[id] ?? {}
            equipment[id]![point] = answer
          } else if (regulatoryUuid(answer.site_uuid) === siteUuid) site[point] = answer
        }
        setSiteAnswers(site)
        setEquipmentAnswers(equipment)
      })
      .catch((reason) => {
        if (live) setError(reason.message)
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [siteUuid, sessionKey, connected, request, version])
  return {
    siteAnswers,
    equipmentAnswersMap,
    loading,
    error,
    connected,
    reload: () => reload((n) => n + 1),
  }
}

export function useRegulatoryEntitySave() {
  const { request } = useRegulatoryRequest()
  const [saving, setSaving] = useState(false)
  const busy = useRef(false)
  const saveEntity = async (
    entity: {
      uuid: import("../../generated/proto-ts/main").UUID
      serializeBinary: () => Uint8Array
    },
    entityType: StorageEntityType,
    parent?: import("../../generated/proto-ts/main").UUID,
  ) => {
    if (busy.current) throw new Error("Un enregistrement est déjà en cours.")
    busy.current = true
    setSaving(true)
    try {
      await request(
        UCPayloadType.SICO_STORAGE_UPDATE_ENTITY,
        new StorageRequest({
          entity_uuid: entity.uuid,
          entity_type: entityType,
          parent_uuid: parent,
          single_entity_data: entity.serializeBinary(),
        }),
      )
      try {
        const payload = await request(
          UCPayloadType.SICO_STORAGE_GET_ENTITY,
          new StorageRequest({ entity_uuid: entity.uuid, entity_type: entityType }),
        )
        return StorageResponse.deserializeBinary(payload.data).single_entity_data
      } catch (reason) {
        throw new Error(
          `Fiche enregistrée, mais sa relecture a échoué. Rechargez la page : ${reason instanceof Error ? reason.message : String(reason)}`,
        )
      }
    } finally {
      busy.current = false
      setSaving(false)
    }
  }
  return { saveEntity, saving }
}

// Regulatory assignments are authoritative in the cloud. Do not let an older BLE
// entity automatically replace them through the general bimodal timestamp sync.
function useRegulatoryEntity<T>(
  entityType: StorageEntityType,
  decode: (bytes: Uint8Array) => T,
): [
  T | null,
  (uuid: import("../../generated/proto-ts/main").UUID | null) => void,
  React.Dispatch<React.SetStateAction<T | null>>,
  string | undefined,
] {
  const { request, connected } = useRegulatoryRequest()
  const { sessionKey } = useRegulatorySession()
  const [uuid, setUuid] = useState("")
  const [value, setValue] = useState<T | null>(null)
  const [error, setError] = useState<string>()
  const load = useCallback((id: import("../../generated/proto-ts/main").UUID | null) => {
    setValue(null)
    setUuid(id ? regulatoryUuid(id) : "")
  }, [])
  useEffect(() => {
    let live = true
    setValue(null)
    setError(undefined)
    if (!uuid) return
    if (!connected) {
      setError("La connexion cloud est nécessaire pour charger cette fiche réglementaire.")
      return
    }
    request(
      UCPayloadType.SICO_STORAGE_GET_ENTITY,
      new StorageRequest({ entity_uuid: uuidToPbUUID(uuid), entity_type: entityType }),
    )
      .then((payload) => {
        if (live)
          setValue(decode(StorageResponse.deserializeBinary(payload.data).single_entity_data))
      })
      .catch((reason) => {
        if (live) setError(reason.message)
      })
    return () => {
      live = false
    }
  }, [uuid, sessionKey, connected, entityType, request, decode])
  return [value, load, setValue, error]
}

export const useRegulatorySite = () =>
  useRegulatoryEntity(StorageEntityType.SITE, Site.deserializeBinary)
export const useRegulatoryEquipment = () =>
  useRegulatoryEntity(StorageEntityType.EQUIPMENT, Equipment.deserializeBinary)

export function useRegulatoryChildren(): [
  Record<string, Equipment[]> | null,
  null,
  null,
  (uuid: import("../../generated/proto-ts/main").UUID | null) => void,
  string | undefined,
] {
  const { request, connected } = useRegulatoryRequest()
  const { sessionKey } = useRegulatorySession()
  const [id, setId] = useState("")
  const [equipmentMap, setEquipmentMap] = useState<Record<string, Equipment[]> | null>(null)
  const [error, setError] = useState<string>()
  const load = useCallback((uuid: import("../../generated/proto-ts/main").UUID | null) => {
    setEquipmentMap(null)
    setId(uuid ? regulatoryUuid(uuid) : "")
  }, [])
  useEffect(() => {
    let live = true
    setEquipmentMap(null)
    setError(undefined)
    if (!id || !connected) return
    request(
      UCPayloadType.SICO_STORAGE_GET_ENTITY_CHILDREN_RECURSIVE,
      new StorageRequest({ entity_type: StorageEntityType.SITE, parent_uuid: uuidToPbUUID(id) }),
    )
      .then((payload) => {
        if (!live) return
        const values = decodeLengthDelimitedArray(
          RawStorageEntity,
          StorageResponse.deserializeBinary(payload.data).multiple_entities_data,
        )
        const map: Record<string, Equipment[]> = { [id]: [] }
        for (const raw of values)
          if (raw.entity_type === StorageEntityType.EQUIPMENT) {
            const parent = regulatoryUuid(raw.parent_uuid)
            map[parent] = map[parent] ?? []
            map[parent]!.push(Equipment.deserializeBinary(raw.data))
          }
        setEquipmentMap(map)
      })
      .catch((reason) => {
        if (live) setError(reason.message)
      })
    return () => {
      live = false
    }
  }, [id, connected, sessionKey, request])
  return [equipmentMap, null, null, load, error]
}
